import React, { CSSProperties, useCallback, useEffect } from 'react';
import { Stack } from '@fluentui/react';
import { Button, Divider } from '@fluentui/react-components';
import { ChatDismiss20Regular } from '@fluentui/react-icons';
import type { ImperativeControlPluginRef } from '@fluentui-copilot/react-copilot';
import { ChatInput as CopilotChatInput, ImperativeControlPlugin } from '@fluentui-copilot/react-copilot';
import { DefenderChatRequest, microsoftConsumerSecurityCopilotClient } from 'client-factories/MicrosoftConsumerSecurityCopilotClient';
import { useAppTheme } from 'common';
import { IStyledComponent } from 'common/Common.types';
import { usePromise } from 'common/hooks/usePromise';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLocalizedStrings } from 'i18n';

import { useCopilot } from './hooks/UseCopilot';

interface IChatInputProps extends IStyledComponent {
  question?: string;
}

// i18next-extract-mark-ns-start copilot
export const ChatInput = (props: IChatInputProps) => {
  const { className, styles, question } = props;
  const { t } = useLocalizedStrings('copilot');
  const copilotSettings = pageDataService.getCopilotSettings();
  const controlRef = React.useRef<ImperativeControlPluginRef>(null);
  const theme = useAppTheme();

  const {
    prompt,
    temperature,
    topP,
    seed,
    useExperimentalModel,
    resolvedHistory,
    artifactId,
    artifactType,
    pushQuestion,
    onResponseFailure,
    onResponseSuccess,
    clearHistory,
  } = useCopilot();

  const scopePromise = usePromise();

  const postQuestion = useCallback(
    (questionText: string) => {
      const itemId = pushQuestion(questionText);
      if (!itemId) {
        return;
      }

      const chatHistory =
        !copilotSettings?.maxChatHistorySize || resolvedHistory.length <= copilotSettings.maxChatHistorySize
          ? resolvedHistory
          : resolvedHistory.slice(resolvedHistory.length - copilotSettings.maxChatHistorySize, resolvedHistory.length);

      const request: DefenderChatRequest = {
        question: questionText,
        history: chatHistory.map((item) => ({ question: item.question, response: item.response })),
        artifactType,
        artifactId: artifactType === undefined ? undefined : artifactId,
      };

      if (!pageDataService.isProdEnvironment()) {
        request.systemPrompt = prompt;
        request.temperature = temperature;
        request.topP = topP;
        request.seed = Number(seed);
        request.useExperimentalModel = useExperimentalModel;
      }

      scopePromise(
        microsoftConsumerSecurityCopilotClient.chat.getChatCompletion({
          body: request,
        })
      )
        .then((result) => {
          onResponseSuccess(itemId, result);
        })
        .catch((e) => {
          onResponseFailure(itemId, e);
        });
    },
    [
      pushQuestion,
      copilotSettings?.maxChatHistorySize,
      resolvedHistory,
      artifactType,
      artifactId,
      scopePromise,
      prompt,
      temperature,
      topP,
      seed,
      useExperimentalModel,
      onResponseSuccess,
      onResponseFailure,
    ]
  );

  useEffect(() => {
    controlRef.current?.setInputText(question || '');
  }, [question]);

  const sendMessage = useCallback(
    (e: React.SyntheticEvent, data: { value?: string }) => {
      if (data.value?.trim().length) {
        postQuestion(data.value);
        controlRef.current?.setInputText('');
      }
    },
    [postQuestion]
  );

  return (
    <CopilotChatInput
      className={className}
      style={{ background: theme.appColors?.white100ToAnchor20, ...(styles?.root as CSSProperties) }}
      onSubmit={sendMessage}
      aria-label={t('ChatControl.ChatInput.AriaLabel')}
      placeholderValue={t('ChatControl.ChatInput.Placeholder')}
      maxLength={copilotSettings?.maxQuestionLength || undefined}
      characterLimitErrorMessage={t('ChatControl.ChatInput.MaxCharacterLimitError')}
      trimWhiteSpace={true}
      showCount={true}
      actions={
        <Stack horizontal verticalAlign="center">
          <Button
            aria-label="ChatControl.ChatInput.ClearAriaLabel"
            appearance="transparent"
            icon={<ChatDismiss20Regular />}
            onClick={clearHistory}
          />
          <Divider style={{ margin: '0 4px' }} vertical />
        </Stack>
      }
    >
      <ImperativeControlPlugin ref={controlRef} />
    </CopilotChatInput>
  );
};
// i18next-extract-mark-ns-stop copilot
